import { useMemo, useState } from 'react';

import { useCreateObject } from '@hooks/useCreateObject';
import { describeMissing, sampleUnitOf } from '@routes/userRoutes/objects/objectTree';
import { useTrainingPermission } from '@routes/userRoutes/projects/singleProject/components/utilityButtons/useTrainingPermission';

import { TRAIN_MODAL, trainModalContent } from './trainModalContent';

const STEP = Object.freeze({ TRAIN: 'train', CREATE: 'create' });

const COUNTS_UPDATING = 'Updating the annotation counts…';
const CREATING = 'Creating the new object…';

const saveFirst = mode => `Save the open ${sampleUnitOf(mode)} first so its boxes are included`;

// Why Train New Object is off, or what it opens. A blocked object still opens its modal,
// so its tooltip says what the modal shows rather than reading like a reason it is off.
// Ready with unsaved boxes, it says up front why the modal's Create will be off.
const tooltipFor = (node, content, mode, countsFetching, creating, hasUnsavedBoxes) => {
	if (!node) return 'Pick an object to annotate first';
	if (creating) return CREATING;
	if (countsFetching) return COUNTS_UPDATING;
	if (content?.kind === TRAIN_MODAL.READY)
		return hasUnsavedBoxes
			? saveFirst(mode)
			: `Create a new object from the ${node.name} annotations`;
	if (content?.kind === TRAIN_MODAL.BLOCKED)
		return `See what training a new object from ${node.name} still needs`;
	return describeMissing(node, mode);
};

/**
 * The Train New Object flow of object annotation, shared by the orthophoto and the
 * single-image sessions: the toolbar button's state, then the modal that says whether
 * the active object has enough training data, then the create form for a new object
 * trained from it. The page hands in its own save (orthophoto closes the tile,
 * single image stays on the image) and renders `TrainNewObjectModals` with `modals`.
 *
 * While a modal is open (`isOpen`) the page's map shortcuts must stand down: they
 * listen on `window`, so Esc or Enter meant for the modal would reach them too.
 *
 * @param {object} options
 * @param {object|null} options.activeNode - the object being annotated, with summary counts
 * @param {string} options.projectMode - a `PROJECT_MODE` value
 * @param {boolean} [options.countsFetching] - the summary is refetching (after a save)
 * @param {() => void} options.onSave - saves the open tile or image
 * @param {boolean} [options.hasUnsavedBoxes] - the open tile or image holds boxes the
 *   backend does not have yet. It drives both modal buttons: Save keeps that work, and
 *   Create waits for it. Not the session's `canSave`, which is also true for a never-saved,
 *   untouched tile: saving that marks it checked and empty, a deliberate toolbar action.
 */
export const useTrainNewObject = ({
	activeNode,
	projectMode,
	countsFetching = false,
	onSave,
	hasUnsavedBoxes = false,
}) => {
	const [step, setStep] = useState(null);
	// The node the create form was opened from, so a summary refetch underneath cannot
	// swap the form's parent. Kept after closing, so the dialog keeps its title and form
	// while it fades out; the next opening sets it again.
	const [parent, setParent] = useState(null);
	const checkPermission = useTrainingPermission();

	const close = () => setStep(null);
	const { createFrom, isPending } = useCreateObject();

	// The host pages re-render on every draw and selection; the node is memoized upstream.
	const content = useMemo(
		() => (activeNode ? trainModalContent(activeNode, projectMode) : null),
		[activeNode, projectMode]
	);
	// Off while a create is pending: the parent is only locked once it lands, so until then
	// a form cancelled and opened again would still offer the same training run.
	const disabled = !content || countsFetching || isPending;

	// The training-data step ends when its content goes (the counts dropped out under
	// it); left open, it would pop back up by itself once they return.
	if (step === STEP.TRAIN && !content) setStep(null);

	const onTrain = () => {
		if (disabled || !checkPermission()) return;
		setStep(STEP.TRAIN);
	};

	return {
		button: {
			onTrain,
			disabled,
			tooltip: tooltipFor(
				activeNode,
				content,
				projectMode,
				countsFetching,
				isPending,
				hasUnsavedBoxes
			),
		},
		// A step without content is reset during render, so this never sees one.
		isOpen: step !== null,
		modals: {
			content,
			trainOpen: step === STEP.TRAIN,
			createOpen: step === STEP.CREATE,
			parent,
			projectMode,
			canSave: hasUnsavedBoxes,
			// The new object trains from saved boxes only, so unsaved ones go in first. A refetch
			// (after a save elsewhere, or on window focus) can change readiness under the modal.
			createDisabledReason: hasUnsavedBoxes
				? saveFirst(projectMode)
				: countsFetching
					? COUNTS_UPDATING
					: null,
			creating: isPending,
			onClose: close,
			onSave: () => {
				close();
				onSave();
			},
			onCreate: () => {
				setParent(activeNode);
				setStep(STEP.CREATE);
			},
			onSubmit: values => {
				if (isPending) return;
				// A form cancelled while its create was pending is already closed.
				createFrom(parent, values, { onSuccess: close });
			},
		},
	};
};
